/**
 * 科技蓝模板 - 注册页
 * 
 * 设计理念：多步骤向导式注册流程
 * - 步骤1：基本信息（用户名、密码、昵称）
 * - 步骤2：联系方式（手机、验证码）
 * - 步骤3：补充信息（自定义字段）
 * - 顶部步骤条指示器 + 淡蓝渐变背景
 * - 完全区别于登录页和红色模板
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

const getSiteId = () => localStorage.getItem('current_site_id') || '';

export type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

export interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

export interface RegisterParams {
  username: string;
  password: string;
  site_id: string;
  phone?: string;
  sms_code?: string;
  [key: string]: string | undefined;
}

async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, { params: { site_id: siteId } });
}

async function registerApi(params: RegisterParams) {
  return request("/auth/register", { method: "POST", body: JSON.stringify(params) });
}

async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", { method: "POST", params: { site_id: siteId }, body: { phone } });
}

async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return request("/upload", { method: "POST", body: formData });
}

const STEPS = [
  { num: 1, title: "基本信息", icon: "ri-user-settings-line" },
  { num: 2, title: "安全验证", icon: "ri-shield-check-line" },
  { num: 3, title: "补充信息", icon: "ri-profile-line" },
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const { register } = useAuth();
  const navigate = useNavigate();
  const formDataRef = useRef<Record<string, string>>({});
  const { siteConfig } = useSiteConfig();
  const siteLogo = siteConfig?.logo || "";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  // 协议弹窗
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>(
    { show: false, title: "", content: "", loading: false }
  );
  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({ show: true, title: page?.title || title, content: page?.content || "暂无内容", loading: false });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);
  const closeAgreement = useCallback(() => setAgreementModal({ show: false, title: "", content: "", loading: false }), []);

  // 短信验证
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        const enabledFields = config.fields.filter((f) => f.is_enabled);
        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => { initialData[field.field_name] = ""; });
        if (config.sms_enabled) {
          initialData['phone'] = ''; initialData['sms_code'] = '';
          setSmsEnabled(true);
        }
        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally { setIsLoadingConfig(false); }
    }
    loadConfig();
  }, []);

  useEffect(() => {
    if (smsCountdown > 0) { const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000); return () => clearTimeout(timer); }
  }, [smsCountdown]);

  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';
    if (!phone) { setError("请先输入手机号"); return; }
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return; }
    try { setSmsLoading(true); await sendSmsCode(getSiteId(), phone); setSmsCountdown(60); setError(""); }
    catch (err: any) { setError(err.message || "短信发送失败"); }
    finally { setSmsLoading(false); }
  }, []);

  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => { const newData = { ...prev, [fieldName]: value }; formDataRef.current = newData; return newData; });
  }, []);

  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try { setUploadingFields((prev) => ({ ...prev, [fieldName]: true })); const result = await uploadFile(file); handleChange(fieldName, result.url); }
    catch (err) { console.error("文件上传失败:", err); alert("文件上传失败，请重试"); }
    finally { setUploadingFields((prev) => ({ ...prev, [fieldName]: false })); }
  }, [handleChange]);

  const basicFields = fields.filter(f => ["username", "password", "confirm_password", "nickname"].includes(f.field_name));
  const customFields = fields.filter(f => !["username", "password", "confirm_password", "nickname"].includes(f.field_name));
  const hasStep3 = customFields.length > 0;

  const validateStep = (s: number) => {
    const d = formDataRef.current;
    if (s === 1) {
      if (!(d.username || "").trim()) { setError("请输入用户名"); return false; }
      if (!/^\w{3,20}$/.test(d.username || "")) { setError("用户名只能包含字母、数字和下划线，3-20位"); return false; }
      if (!(d.password || "").trim() || (d.password || "").length < 6) { setError("密码至少6位"); return false; }
      if (d.password !== d.confirm_password) { setError("两次密码不一致"); return false; }
    }
    if (s === 2 && smsEnabled) {
      const phone = d['phone'] || '';
      if (!phone) { setError("请输入手机号"); return false; }
      if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return false; }
      if (!(d['sms_code'] || "").trim()) { setError("请输入短信验证码"); return false; }
      if ((d['sms_code'] || "").length !== 6) { setError("短信验证码为6位数字"); return false; }
    }
    return true;
  };

  const handleNext = () => {
    setError("");
    if (!validateStep(step)) return;
    if (step === 2 && !hasStep3) {
      if (step === 2 && smsEnabled === false && hasStep3 === false) { /* last step */ }
    }
    setStep(Math.min(step + 1, 3));
  };

  const handlePrev = () => { setError(""); setStep(Math.max(step - 1, 1)); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!agreed) { setError("请先阅读并同意注册协议和隐私政策"); return; }

    for (const field of fields) {
      if (field.is_required && !(formDataRef.current[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`); return;
      }
    }

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;
      const params: RegisterParams = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || '',
      };
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name))
          params[field.field_name] = currentData[field.field_name] ?? '';
      });
      if (smsEnabled) { params.phone = currentData['phone']; params.sms_code = currentData['sms_code']; }
      await registerApi(params);
      navigate("/");
    } catch (err: any) { setError(err.message || "注册失败"); }
    finally { setIsLoading(false); }
  };

  const renderField = (field: RegisterField, color: string = "cyan") => {
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;
    const ringCls = `focus:ring-${color}-400`;

    const baseInputClass = `w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 ${ringCls} focus:border-transparent transition-all`;

    switch (field.field_type) {
      case "textarea":
        return <textarea key={field.field_name} value={value} onChange={(e) => handleChange(field.field_name, e.target.value)} placeholder={field.placeholder || field.hint || `请输入${field.field_label}`} rows={3} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent resize-none" />;
      case "select":
        return <select key={field.field_name} value={value} onChange={(e) => handleChange(field.field_name, e.target.value)} className={baseInputClass}>
          <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
          {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>;
      case "radio":
        return <div key={field.field_name} className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {field.options?.map((o) => (
            <label key={o} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${value === o ? "border-cyan-300 bg-cyan-50 ring-1 ring-cyan-200" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
              <input type="radio" name={field.field_name} value={o} checked={value === o} onChange={(e) => handleChange(field.field_name, e.target.value)} className="w-4 h-4 text-cyan-500" />
              <span className="text-sm">{o}</span>
            </label>
          ))}
        </div>;
      case "checkbox":
        return <div key={field.field_name} className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {field.options?.map((o) => (
            <label key={o} className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" name={field.field_name} value={o} checked={(value || "").split(",").includes(o)} onChange={(e) => { const cur = value ? value.split(",") : []; const nxt = e.target.checked ? [...cur, o] : cur.filter(v => v !== o); handleChange(field.field_name, nxt.join(",")); }} className="w-4 h-4 text-cyan-500 rounded" />
              <span className="text-sm">{o}</span>
            </label>
          ))}
        </div>;
      case "date": return <input key={field.field_name} type="date" value={value} onChange={(e) => handleChange(field.field_name, e.target.value)} className={baseInputClass} />;
      case "number": return <input key={field.field_name} type="number" value={value} onChange={(e) => handleChange(field.field_name, e.target.value)} placeholder={field.placeholder || field.hint || `请输入${field.field_label}`} className={baseInputClass} />;
      case "file":
      case "image":
        return <div key={field.field_name}>
          <label htmlFor={`file-${field.field_name}`} className="flex flex-col items-center justify-center w-full px-4 py-6 bg-white border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-cyan-300 hover:bg-cyan-50/50 transition-all">
            {uploadingFields[field.field_name] ? <div className="flex flex-col items-center"><div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mb-2" /><span className="text-sm text-gray-500">上传中...</span></div>
              : value ? <div className="flex flex-col items-center">
                {field.field_type === "image" ? <img src={value} alt="预览" className="w-24 h-24 object-cover rounded-lg mb-2" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <i className="ri-file-check-line text-3xl text-green-500 mb-2" />}
                <span className="text-sm text-gray-600">点击重新上传</span>
              </div>
                : <div className="flex flex-col items-center"><i className="ri-upload-cloud-2-line text-3xl text-gray-300 mb-2" /><span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span></div>}
          </label>
          <input id={`file-${field.field_name}`} type="file" accept={field.field_type === "image" ? "image/*" : "*/*"} onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload(field.field_name, file); }} className="hidden" />
          {value && <button type="button" onClick={() => handleChange(field.field_name, "")} className="mt-2 text-sm text-cyan-600 hover:text-cyan-700">清除</button>}
        </div>;
      default:
        return (
          <div className="relative">
            <input key={field.field_name} type={isPassword ? (showPassword ? "text" : "password") : "text"} value={value} onChange={(e) => handleChange(field.field_name, e.target.value)} placeholder={field.placeholder || field.hint || `请输入${field.field_label}`} className={isPassword ? `${baseInputClass} pr-11` : baseInputClass} />
            {isPassword && <button type="button" onClick={() => togglePasswordVisibility(field.field_name)} className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-md"><i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} /></button>}
          </div>
        );
    }
  };

  const getFieldIcon = (type: FieldType) => {
    const map: Record<string, string> = { text: "ri-edit-line", number: "ri-edit-line", textarea: "ri-file-text-line", select: "ri-list-check", radio: "ri-list-check", checkbox: "ri-list-check", password: "ri-lock-line", date: "ri-calendar-line", file: "ri-image-add-line", image: "ri-image-add-line" };
    return map[type] || "ri-edit-line";
  };

  const isWideField = (field: RegisterField) => ["textarea", "file", "image", "radio", "checkbox"].includes(field.field_type);

  if (isLoadingConfig) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="text-center"><i className="ri-loader-4-line animate-spin text-3xl text-cyan-500" /><p className="mt-2 text-gray-500 text-sm">加载中...</p></div></div>;
  }

  const totalSteps = hasStep3 ? 3 : (smsEnabled ? 2 : 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50">
      {/* 顶部导航 */}
      <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            {siteLogo && <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center p-1 overflow-hidden"><img src={siteLogo} alt={siteDisplayName} className="w-full h-full object-contain" /></div>}
            <span className="text-lg font-bold text-gray-800">{siteDisplayName}</span>
          </Link>
          <Link to="/login" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">已有账户？去登录</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* 步骤指示器 */}
        <div className="mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">创建您的账户</h1>
          <div className="flex items-center justify-center gap-0">
            {STEPS.filter((_, i) => i < totalSteps).map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s.num ? "bg-cyan-600 text-white shadow-lg shadow-cyan-200" : "bg-gray-100 text-gray-400"}`}>
                    {step > s.num ? <i className="ri-check-line" /> : s.num}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${step >= s.num ? "text-cyan-600" : "text-gray-400"}`}>{s.title}</span>
                </div>
                {idx < totalSteps - 1 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-2 mt-[-14px] rounded transition-colors ${step > s.num ? "bg-cyan-600" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 表单卡片 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 max-w-2xl mx-auto">
          <form onSubmit={handleSubmit}>
            {/* 步骤1：基本信息 */}
            {step === 1 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-cyan-100 flex items-center justify-center"><i className="ri-user-settings-line text-cyan-600" /></div>
                  <div><h3 className="text-lg font-semibold text-gray-800">基本信息</h3><p className="text-xs text-gray-400">设置您的登录账户信息</p></div>
                </div>
                <div className="space-y-4">
                  {basicFields.map((field) => (
                    <div key={field.field_name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.field_label}{field.is_required && <span className="text-cyan-500 ml-0.5">*</span>}</label>
                      <div className="relative">
                        {!["textarea", "file", "image"].includes(field.field_type) && <i className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`} />}
                        {renderField(field)}
                      </div>
                      {field.hint && <p className="mt-1 text-xs text-gray-400">{field.hint}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 步骤2：安全验证 */}
            {step === 2 && smsEnabled && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-cyan-100 flex items-center justify-center"><i className="ri-shield-check-line text-cyan-600" /></div>
                  <div><h3 className="text-lg font-semibold text-gray-800">安全验证</h3><p className="text-xs text-gray-400">绑定手机号以保障账户安全</p></div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">手机号 <span className="text-cyan-500">*</span></label>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="tel" value={formData['phone'] || ''} onChange={(e) => { handleChange('phone', e.target.value); setError(""); }} placeholder="请输入手机号" className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">短信验证码 <span className="text-cyan-500">*</span></label>
                    <div className="relative">
                      <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="text" value={formData['sms_code'] || ''} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); handleChange('sms_code', v); setError(""); }} placeholder="请输入6位验证码" maxLength={6} className="w-full pl-10 pr-28 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent tracking-widest transition-all" />
                      <button type="button" onClick={handleSendSmsCode} disabled={smsCountdown > 0 || smsLoading} className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors whitespace-nowrap">
                        {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 步骤2没有短信时跳转到步骤3 */}
            {step === 2 && !smsEnabled && <div className="text-center py-8 text-gray-400 text-sm">无需手机验证，继续下一步</div>}

            {/* 步骤3：补充信息 */}
            {step === 3 && customFields.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-xl bg-cyan-100 flex items-center justify-center"><i className="ri-profile-line text-cyan-600" /></div>
                  <div><h3 className="text-lg font-semibold text-gray-800">补充信息</h3><p className="text-xs text-gray-400">完善个人资料以获得更好体验</p></div>
                </div>
                <div className="space-y-4">
                  {customFields.map((field) => (
                    <div key={field.field_name} className={isWideField(field) ? "" : ""}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.field_label}{field.is_required && <span className="text-cyan-500 ml-0.5">*</span>}</label>
                      <div className="relative">
                        {!["textarea", "file", "image", "radio", "checkbox"].includes(field.field_type) && <i className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`} />}
                        {renderField(field)}
                      </div>
                      {field.hint && <p className="mt-1 text-xs text-gray-400">{field.hint}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 错误提示 */}
            {error && (
              <div className="mt-6 p-4 bg-cyan-50 text-cyan-700 text-sm rounded-xl flex items-center gap-3 border border-cyan-100">
                <i className="ri-error-warning-line shrink-0 text-lg" />
                <span>{error}</span>
              </div>
            )}

            {/* 最后一步：协议+提交 */}
            {(step === totalSteps || (step === 2 && !smsEnabled && !hasStep3)) && (
              <div className="mt-8 space-y-4">
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer ${agreed ? "border-cyan-200 bg-cyan-50/50" : "border-gray-200 bg-gray-50/50 hover:bg-gray-50"}`}
                  onClick={() => setAgreed(!agreed)}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${agreed ? "border-cyan-600 bg-cyan-600" : "border-gray-300 bg-white"}`}>
                    {agreed && <i className="ri-check-line text-white text-xs" />}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed select-none">
                    我已阅读并同意
                    <span className="text-cyan-600 hover:underline mx-1 cursor-pointer font-medium" onClick={(e) => { e.stopPropagation(); openAgreement("register-agreement", "注册协议"); }}>《注册协议》</span>
                    和
                    <span className="text-cyan-600 hover:underline mx-1 cursor-pointer font-medium" onClick={(e) => { e.stopPropagation(); openAgreement("privacy", "隐私政策"); }}>《隐私政策》</span>
                  </p>
                </div>

                <button type="submit" disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold rounded-xl hover:from-cyan-700 hover:to-teal-700 transition-all disabled:opacity-50 shadow-lg shadow-cyan-500/25 text-base">
                  {isLoading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />注册中...</span> : "完成注册"}
                </button>
              </div>
            )}

            {/* 导航按钮 */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
              {step > 1 ? (
                <button type="button" onClick={handlePrev} className="flex items-center gap-1.5 px-5 py-2.5 text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm font-medium transition-colors">
                  <i className="ri-arrow-left-line" />上一步
                </button>
              ) : <div />}

              {step < totalSteps && (
                <button type="button" onClick={handleNext} className="flex items-center gap-1.5 px-5 py-2.5 bg-cyan-600 text-white hover:bg-cyan-700 rounded-xl text-sm font-medium transition-colors shadow-sm ml-auto">
                  下一步<i className="ri-arrow-right-line" />
                </button>
              )}
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-gray-400 mt-8">
          已有账户？<Link to="/login" className="text-cyan-600 font-semibold hover:text-cyan-700 underline underline-offset-2">立即登录</Link>
        </p>
      </div>

      {/* 协议弹窗 */}
      {agreementModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeAgreement}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><i className="ri-file-text-line text-cyan-500" />{agreementModal.title}</h2>
              <button onClick={closeAgreement} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? <div className="flex items-center justify-center py-16"><div className="text-center"><i className="ri-loader-4-line animate-spin text-2xl text-cyan-400" /><p className="mt-2 text-sm text-gray-400">加载中...</p></div></div>
                : <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: agreementModal.content }} />}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
              <button onClick={closeAgreement} className="px-6 py-2.5 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 text-sm">已了解，关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
